import React from 'react';
import { Chapter } from '@/types';
import { formatDuration } from '@/utils/format';

interface ChapterListProps {
  chapters: Chapter[];
  activeChapterId?: string;
  onChapterSelect: (chapter: Chapter) => void;
  chapterLabel?: string; // e.g. "Passage" instead of "Chapter"
}

const ChapterList: React.FC<ChapterListProps> = ({
  chapters,
  activeChapterId,
  onChapterSelect,
  chapterLabel = 'Chapter',
}) => {
  return (
    <div className="divide-y divide-[var(--border-light)]">
      {chapters.map((chapter) => {
        const isActive = activeChapterId === chapter.id;

        return (
          <button
            key={chapter.id}
            onClick={() => onChapterSelect(chapter)}
            className={`sp-chapter-row w-full group flex items-center gap-4 px-4 py-3.5 text-left ${
              isActive ? 'active' : ''
            }`}
          >
            {/* Left: chapter number or playing indicator */}
            <div className="w-7 flex-shrink-0 flex items-center justify-center">
              {isActive ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="var(--accent-primary)">
                  <rect x="1" y="4" width="2" height="8" rx="0.5">
                    <animate attributeName="height" values="8;4;8" dur="1s" repeatCount="indefinite" />
                    <animate attributeName="y" values="4;6;4" dur="1s" repeatCount="indefinite" />
                  </rect>
                  <rect x="5" y="2" width="2" height="10" rx="0.5">
                    <animate attributeName="height" values="10;6;10" dur="0.8s" repeatCount="indefinite" />
                    <animate attributeName="y" values="2;5;2" dur="0.8s" repeatCount="indefinite" />
                  </rect>
                  <rect x="9" y="3" width="2" height="9" rx="0.5">
                    <animate attributeName="height" values="9;3;9" dur="1.2s" repeatCount="indefinite" />
                    <animate attributeName="y" values="3;7;3" dur="1.2s" repeatCount="indefinite" />
                  </rect>
                </svg>
              ) : (
                <>
                  {/* Number shown by default, play icon on hover */}
                  <span className="text-sm text-[var(--text-secondary)] group-hover:hidden">
                    {chapter.chapter_num}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="var(--text-primary)"
                    className="hidden group-hover:block"
                  >
                    <path d="M3 1.713a.7.7 0 0 1 1.05-.607l10.89 6.288a.7.7 0 0 1 0 1.212L4.05 14.894A.7.7 0 0 1 3 14.288V1.713z"/>
                  </svg>
                </>
              )}
            </div>

            {/* Middle: title and duration */}
            <div className="flex-1 min-w-0">
              <p className={`text-base sm:text-lg font-semibold truncate ${
                isActive ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'
              }`}>
                {/* For Thomas and Mary, chapter titles are already beautiful like "Saying 1" or "The Vision", so we don't need the prefix */}
                {chapter.title.startsWith('Saying') || chapter.title.includes('(') ? chapter.title : `${chapterLabel} ${chapter.chapter_num}: ${chapter.title}`}
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {formatDuration(chapter.total_duration_seconds)}
              </p>
            </div>

            {/* Right: play button on hover */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              isActive
                ? 'bg-[var(--accent-primary)] opacity-100'
                : 'border border-[var(--border)] opacity-0 group-hover:opacity-100 hover:border-[var(--text-primary)] hover:scale-105'
            }`}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill={isActive ? 'white' : 'var(--text-primary)'}
              >
                <path d="M3 1.713a.7.7 0 0 1 1.05-.607l10.89 6.288a.7.7 0 0 1 0 1.212L4.05 14.894A.7.7 0 0 1 3 14.288V1.713z"/>
              </svg>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ChapterList;
